import { Button, Flex, Text, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useGetGpDocumentDownloadUrlLazyQuery } from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import { navigateDownloadWindow } from "./documentDownload";
import type { ProposedChangeMetadata } from "../types";

type EumMetadataBlockProps = {
  metadata: ProposedChangeMetadata;
  changeRequestId: string;
};

const getFileIconType = (fileName: string): "file-pdf" | "file-alt" =>
  fileName.toLowerCase().endsWith(".pdf") ? "file-pdf" : "file-alt";

const MetadataField = ({ label, value }: { label: string; value: string }) => (
  <Flex flexDirection="column" gap={SPACING.X_SMALL}>
    <Text variant="strong-md" color={Colors.Neutral900}>
      {label}
    </Text>
    <Text variant="regular-md" color={Colors.Neutral900}>
      {value}
    </Text>
  </Flex>
);

export const EumMetadataBlock = ({ metadata, changeRequestId }: EumMetadataBlockProps) => {
  const { reasonCode, details, supportingDocumentName, s3DocumentKey } = metadata;

  const [fetchDownloadUrl, { loading: downloadLoading }] = useGetGpDocumentDownloadUrlLazyQuery(
    {
      fetchPolicy: "network-only",
      onError: (error) => toast.error(error.message),
    },
  );

  const handleDownload = async (documentKey: string) => {
    const downloadWindow = window.open("", "_blank");
    const result = await fetchDownloadUrl({
      variables: { input: { changeRequestId, s3DocumentKey: documentKey } },
    });
    const getUrl = result.data?.getGpDocumentDownloadUrl.getUrl;
    navigateDownloadWindow(downloadWindow, getUrl);
  };

  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM} data-testid="eum-metadata-block">
      {reasonCode && <MetadataField label="Reason code" value={reasonCode} />}
      {details && <MetadataField label="Details" value={details} />}
      {supportingDocumentName && (
        <Flex flexDirection="column" gap={SPACING.X_SMALL}>
          <Text variant="strong-md" color={Colors.Neutral900}>
            Supporting document:
          </Text>
          <Flex alignItems="center" gap={SPACING.SMALL}>
            <Icon
              type={getFileIconType(supportingDocumentName)}
              variant="solid"
              size="medium"
              color={Colors.Danger600}
            />
            <Text variant="regular-md" color={Colors.Neutral900}>
              {supportingDocumentName}
            </Text>
            {s3DocumentKey && (
              <Button
                variant="link"
                data-testid="supporting-document-download"
                disabled={downloadLoading}
                onClick={() => void handleDownload(s3DocumentKey)}
              >
                Download
              </Button>
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
